import React, { useState, useEffect, useRef } from "react";
import claudeLogo from "../../../public/img/claude-color.svg";
import "./tools/tools.css";
import BashTool from "./tools/BashTool";
import ReadTool from "./tools/ReadTool";
import WriteTool from "./tools/WriteTool";
import EditTool from "./tools/EditTool";
import GlobTool from "./tools/GlobTool";
import GrepTool from "./tools/GrepTool";
import WebSearchTool from "./tools/WebSearchTool";
import WebFetchTool from "./tools/WebFetchTool";
import McpToolCard from "./tools/McpToolCard";
import GenericToolCard from "./tools/GenericToolCard";
import EnvTool from "./tools/EnvTool";

const RENDERERS = {
  bash: BashTool,
  read: ReadTool,
  write: WriteTool,
  edit: EditTool,
  glob: GlobTool,
  grep: GrepTool,
  web_search: WebSearchTool,
  web_fetch: WebFetchTool,
  env_read_file: EnvTool,
  env_write_file: EnvTool,
  env_list_files: EnvTool,
  env_delete_file: EnvTool,
};

function formatElapsed(ms) {
  const total = Math.max(0, Math.floor(ms / 1000));
  if (total < 60) return `${total}s`;
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}m ${s}s`;
}

function ToolCallCard({
  event, result, showAvatar = true,
  awaitingConfirmation = false, onConfirm,
}) {
  const isMcp = event.type === "agent.mcp_tool_use";
  const name = event.payload?.name;
  const Renderer = isMcp ? McpToolCard : (RENDERERS[name] || GenericToolCard);

  // Local optimistic decision so the card updates the instant the user clicks,
  // before the agent's continuation streams back.
  const [decision, setDecision] = useState(null); // "allow" | "deny" | null
  const awaiting = awaitingConfirmation && !decision;

  const isRunning = !result;
  let status;
  if (awaiting) status = "awaiting";
  else if (decision === "deny" && isRunning) status = "denied";
  else status = isRunning ? "running" : (result.payload?.is_error ? "error" : "ok");

  const decide = async (choice) => {
    setDecision(choice);
    try {
      await onConfirm?.(event.event_id, choice);
    } catch (_) {
      setDecision(null); // the confirmation never landed: bring the buttons back
    }
  };

  // Auto-expand while running or awaiting approval; collapse once resolved.
  const [expanded, setExpanded] = useState(isRunning);
  const userToggledRef = useRef(false);
  useEffect(() => {
    if (userToggledRef.current) return;
    setExpanded(isRunning || awaiting);
  }, [isRunning, awaiting]);
  const handleToggle = () => {
    userToggledRef.current = true;
    setExpanded((v) => !v);
  };

  // Elapsed time: derive from the persisted event timestamps so it survives a
  // page refresh (the old client-clock approach reset everything to 0s on
  // reload, since replayed events arrive already-finished). Fall back to the
  // client clock for a live run before the result timestamp exists.
  const startTs = event.processed_at ? Date.parse(event.processed_at) : NaN;
  const endTs = result?.processed_at ? Date.parse(result.processed_at) : NaN;
  const startRef = useRef(Date.now());
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!isRunning) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [isRunning]);
  let elapsedMs;
  if (!Number.isNaN(startTs) && !Number.isNaN(endTs)) {
    elapsedMs = Math.max(0, endTs - startTs); // persisted server timing
  } else if (isRunning) {
    const start = Number.isNaN(startTs) ? startRef.current : startTs;
    elapsedMs = Math.max(0, Date.now() - start); // live tick
  } else {
    elapsedMs = 0; // finished but missing timestamps (legacy events)
  }

  const summary = Renderer.summarize ? Renderer.summarize(event.payload) : (name || "tool call");

  return (
    <div className={`ca-step ca-step--tool ${showAvatar ? "ca-step--lead" : ""}`}>
      <div className="ca-msg-avatar" aria-hidden="true">
        {showAvatar && <img src={claudeLogo} alt="" />}
      </div>
      <div className="ca-step-main">
        <div className={`tool-card tool-card--${status}`}>
          <button className="tool-card-header" onClick={handleToggle}>
            <span className={`tool-card-status tool-card-status--${status}`}>
              {status === "running" && <span className="tool-card-spinner" />}
              {status === "awaiting" && <span className="tool-card-await">?</span>}
              {status === "ok" && <span className="tool-card-check">✓</span>}
              {status === "error" && <span className="tool-card-x">✗</span>}
              {status === "denied" && <span className="tool-card-x">⊘</span>}
            </span>
            <span className="tool-card-name">{isMcp && event.payload?.server_name ? `${event.payload.server_name}:${name}` : name}</span>
            <span className="tool-card-summary">{summary}</span>
            <span className="tool-card-elapsed">{formatElapsed(elapsedMs)}</span>
            <svg width="9" height="9" viewBox="0 0 12 12" className="tool-card-caret"
                 style={{ transform: `rotate(${expanded ? 90 : 0}deg)`, transition: "transform 140ms ease" }}
                 aria-hidden="true">
              <path d="M4.5 3.5 7 6 4.5 8.5" stroke="currentColor" strokeWidth="1.3"
                    fill="none" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          {expanded && <Renderer payload={event.payload} result={result?.payload} />}
          {awaiting && (
            <div className="tool-card-confirm">
              <span className="tool-card-confirm-label">Allow this action?</span>
              <div className="tool-card-confirm-actions">
                <button
                  type="button"
                  className="tool-confirm-btn tool-confirm-btn--allow"
                  onClick={() => decide("allow")}
                >Approve</button>
                <button
                  type="button"
                  className="tool-confirm-btn tool-confirm-btn--deny"
                  onClick={() => decide("deny")}
                >Deny</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Memoized: a tool card (which may render Prism-highlighted output) must not
// re-render every time another event streams in. Props are stable per card;
// only its own paired `result`/confirmation state changes.
export default React.memo(ToolCallCard);
